import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  createUserWithEmailAndPassword,
  getAuth,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
} from "firebase/auth";
import { doc, getFirestore, setDoc } from "firebase/firestore";

const LOGIN = "login";
const SIGN_UP = "signup";
const RESET_PASS = "reset";

const IconLock = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <rect x="2.5" y="6" width="9" height="6.5" rx="1" stroke="currentColor" strokeWidth="1.3" />
    <path d="M4.5 6V4a2.5 2.5 0 0 1 5 0v2" stroke="currentColor" strokeWidth="1.3" />
  </svg>
);

const ArrowRight = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path d="M1 7h12m-4-4 4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

function Login() {
  const navigate = useNavigate();
  const [authType, setAuthType] = React.useState(SIGN_UP);

  const [email, setEmail] = React.useState("");
  const [firstName, setFirstName] = React.useState("");
  const [lastName, setLastName] = React.useState("");
  const [password, setPassword] = React.useState("");

  const [emailError, setEmailError] = React.useState("");
  const [firstNameError, setFirstNameError] = React.useState("");
  const [lastNameError, setLastNameError] = React.useState("");
  const [passwordError, setPasswordError] = React.useState("");

  useEffect(() => {
    const userEmail = localStorage.getItem("preferredTitleEmail");
    if (userEmail) {
      setEmail(userEmail);
      setAuthType(LOGIN);
    }
  }, []);

  const isValid = React.useMemo(() => {
    return !(emailError || firstNameError || lastNameError || passwordError);
  }, [emailError, firstNameError, lastNameError, passwordError]);

  const submit = async () => {
    localStorage.setItem("realmEmail", email);
    try {
      switch (authType) {
        case LOGIN:
          await signInWithEmailAndPassword(getAuth(), email, password);
          navigate("/secure-portal");
          break;
        case SIGN_UP:
          validateAll();
          if (isValid) {
            const user = await createUserWithEmailAndPassword(getAuth(), email, password);
            const userRef = doc(getFirestore(), "users", user.user.uid);
            await setDoc(userRef, {
              email,
              timeCreated: new Date(),
              lastLogin: new Date(),
              firstName,
              lastName,
              isAdmin: false,
            });
            navigate("/secure-portal");
          }
          break;
        case RESET_PASS:
          await sendPasswordResetEmail(getAuth(), email);
          toast.success(`Reset password instructions sent to ${email}`, {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: true,
            closeOnClick: true,
            pauseOnHover: false,
            draggable: false,
          });
          setAuthType(LOGIN);
          break;
      }
    } catch (err) {
      setEmail("");
      setPassword("");
      let message = (err as any).message;
      if (message.includes("may have been deleted")) {
        message = "A user with that email does not exist.";
      }
      toast.error(message, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: false,
        draggable: false,
      });
    }
  };

  React.useEffect(() => {
    if (authType === LOGIN) {
      setEmailError("");
      setFirstNameError("");
      setLastNameError("");
      setPasswordError("");
    }
  }, [authType]);

  const validateAll = () => {
    validateEmail();
    validateFirstName();
    validateLastName();
    validatePassword();
  };

  const validateEmail = () => {
    if (authType === SIGN_UP && !(email.includes("@") && email.includes(".") && email.length > 4)) {
      setEmailError("Invalid email.");
    }
  };
  const validateFirstName = () => {
    if (authType === SIGN_UP && firstName.length < 1) setFirstNameError("Please enter a first name.");
  };
  const validateLastName = () => {
    if (authType === SIGN_UP && lastName.length < 1) setLastNameError("Please enter a last name.");
  };
  const validatePassword = () => {
    if (authType === SIGN_UP && password.length < 6) setPasswordError("Password must be at least 6 characters.");
  };

  const handleKey = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") submit();
  };

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1.1fr", minHeight: "100vh" }}>
      {/* Left — brand panel */}
      <div style={{
        background: "var(--maroon-deep)",
        color: "var(--cream)",
        padding: "48px 56px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        position: "relative",
        overflow: "hidden",
      }}>
        <div style={{ position: "absolute", inset: 0, opacity: 0.15, background: "radial-gradient(ellipse at 70% 30%, var(--gold), transparent 60%)" }} />

        <div style={{ position: "relative" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div className="p-brand-mark">P|T</div>
            <div className="p-brand-name">
              Preferred Title
              <small>Secure Portal</small>
            </div>
          </div>
        </div>

        <div style={{ position: "relative" }}>
          <div className="p-pill maroon" style={{ background: "rgba(232,169,60,.15)", color: "var(--gold)", marginBottom: 20 }}>
            <span className="dot" style={{ background: "var(--gold)" }} /> Bank-grade encryption
          </div>
          <h2 style={{
            fontFamily: "var(--serif)",
            fontSize: 48,
            lineHeight: 1.05,
            color: "var(--cream)",
            fontWeight: 500,
            marginBottom: 20,
            letterSpacing: "-0.01em",
          }}>
            Closing documents,<br />
            <span style={{ fontStyle: "italic", color: "var(--gold)" }}>safe and sorted.</span>
          </h2>
          <p style={{ fontSize: 15, opacity: 0.8, lineHeight: 1.55, maxWidth: 420, margin: 0 }}>
            Sign in to manage cases, share documents securely with buyers and lenders, and track every settlement from contract to closing.
          </p>
        </div>

        <div style={{ position: "relative", display: "flex", alignItems: "center", gap: 16, fontSize: 12, color: "rgba(246,237,228,.6)" }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <IconLock /> 256-bit TLS
          </span>
          <span style={{ width: 3, height: 3, borderRadius: "50%", background: "rgba(246,237,228,.4)" }} />
          <span>SOC 2 audited</span>
          <span style={{ width: 3, height: 3, borderRadius: "50%", background: "rgba(246,237,228,.4)" }} />
          <span>ALTA Best Practices</span>
        </div>
      </div>

      {/* Right — form */}
      <div style={{
        background: "var(--paper)",
        padding: "48px 80px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
      }}>
        <div style={{ maxWidth: 420 }}>
          {authType !== RESET_PASS && (
            <div style={{ display: "flex", gap: 4, padding: 4, background: "var(--cream)", borderRadius: 999, marginBottom: 32, width: "fit-content" }}>
              {[{ k: LOGIN, l: "Sign in" }, { k: SIGN_UP, l: "Create account" }].map((t) => (
                <button
                  key={t.k}
                  onClick={() => setAuthType(t.k)}
                  style={{
                    padding: "8px 18px",
                    borderRadius: 999,
                    fontSize: 13,
                    fontWeight: 500,
                    cursor: "pointer",
                    background: authType === t.k ? "var(--paper)" : "transparent",
                    color: authType === t.k ? "var(--maroon)" : "var(--ink-soft)",
                    boxShadow: authType === t.k ? "0 1px 3px rgba(0,0,0,.08)" : "none",
                    border: "none",
                    fontFamily: "var(--sans)",
                  }}
                >
                  {t.l}
                </button>
              ))}
            </div>
          )}

          <h1 style={{ fontFamily: "var(--serif)", fontSize: 40, color: "var(--maroon)", marginBottom: 8, letterSpacing: "-0.01em", fontWeight: 500 }}>
            {authType === RESET_PASS ? "Reset your password." : authType === LOGIN ? "Welcome back." : "Create your account."}
          </h1>
          <p style={{ fontSize: 14, color: "var(--ink-soft)", marginBottom: 32 }}>
            {authType === RESET_PASS
              ? "Enter your email and we'll send reset instructions."
              : authType === LOGIN
              ? "Sign in to continue to your secure case workspace."
              : "A few details and you're set up."}
          </p>

          <div style={{ display: "flex", flexDirection: "column", gap: 16 }} onKeyDown={handleKey}>
            {authType === SIGN_UP && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div className="p-field">
                  <label className="p-label">First name</label>
                  <input className="p-input" placeholder="Sarah" value={firstName} onChange={(e) => { setFirstName(e.target.value); if (firstNameError) setFirstNameError(""); }} onBlur={validateFirstName} />
                  {firstNameError && <div style={{ fontSize: 12, color: "var(--maroon)" }}>{firstNameError}</div>}
                </div>
                <div className="p-field">
                  <label className="p-label">Last name</label>
                  <input className="p-input" placeholder="Patel" value={lastName} onChange={(e) => { setLastName(e.target.value); if (lastNameError) setLastNameError(""); }} onBlur={validateLastName} />
                  {lastNameError && <div style={{ fontSize: 12, color: "var(--maroon)" }}>{lastNameError}</div>}
                </div>
              </div>
            )}

            <div className="p-field">
              <label className="p-label">Email</label>
              <input className="p-input" type="email" placeholder="you@example.com" value={email} onChange={(e) => { setEmail(e.target.value); if (emailError) setEmailError(""); }} onBlur={validateEmail} />
              {emailError && <div style={{ fontSize: 12, color: "var(--maroon)" }}>{emailError}</div>}
            </div>

            {authType !== RESET_PASS && (
              <div className="p-field">
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <label className="p-label">Password</label>
                  {authType === LOGIN && (
                    <button onClick={() => setAuthType(RESET_PASS)} style={{ fontSize: 12, color: "var(--maroon)", background: "none", border: "none", cursor: "pointer", padding: 0, fontFamily: "var(--sans)" }}>
                      Forgot password?
                    </button>
                  )}
                </div>
                <input className="p-input" type="password" placeholder="••••••••••••" value={password} onChange={(e) => { setPassword(e.target.value); if (passwordError) setPasswordError(""); }} onBlur={validatePassword} />
                {passwordError && <div style={{ fontSize: 12, color: "var(--maroon)" }}>{passwordError}</div>}
              </div>
            )}

            <button
              className="p-btn p-btn-primary"
              style={{ padding: "14px", justifyContent: "center", marginTop: 8, fontSize: 14, width: "100%" }}
              onClick={submit}
            >
              {authType === RESET_PASS ? "Send reset email" : authType === LOGIN ? "Sign in" : "Create account"} <ArrowRight />
            </button>

            {authType === RESET_PASS && (
              <button className="p-btn p-btn-ghost" style={{ justifyContent: "center" }} onClick={() => setAuthType(LOGIN)}>
                Back to sign in
              </button>
            )}
          </div>

          <p style={{ fontSize: 11, color: "var(--ink-soft)", textAlign: "center", marginTop: 32, lineHeight: 1.6 }}>
            By continuing you agree to our Terms and acknowledge our<br />
            Privacy Policy. Unauthorized access is prohibited.
          </p>
        </div>
      </div>
    </div>
  );
}

export default Login;
